import React from 'react';
import { FontsLoaderEmbedded } from 'pxc-editor-material-svg-rendering/components/InteractiveRenderer/FontsLoader';
import { hasShapeContent } from 'pxc-editor-core/helpers/schema';
import { rotateRect90deg } from 'pxc-editor-core/helpers/calculation';
import { isMultiColored as isMaterialMultiColored } from 'pxc-editor-core/helpers/material';
import { safeObjCopy } from 'pxc-editor-core/helpers/common';
import store from '@/store';
import { colors } from '@/globalStyles';
import { changeContentColor, createClassName, getMatrixShield } from '@/helpers';
import { DEFAULT_PRINT_RESOLUTION_DPMM, PRINTING_MODE } from '../../../constants';
import { dpiToDpmm } from '../../../helpers/common';
import PrimitiveRenderer from '../../shared/PrimitiveRenderer';
import SectionRenderer from '../../shared/SectionRenderer';

/**
 * IMPORTANT to know:
 * for monochrome material colors without invertion:
 *  - paper color = 'white'
 *  - ribbon color = 'black'
 *
 */

export default class BasePrintRenderer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.printingQuality = DEFAULT_PRINT_RESOLUTION_DPMM;

    this.isPreviewPrint = false;
    this.svgNodeRef = React.createRef();
    this.onRendered = this.onRendered.bind(this);
  }

  checkBaseProps() {
    const { resolution, mode: printMode } = this.props;

    this.isPreviewPrint = printMode === PRINTING_MODE.PREVIEW;
    this.printingQuality = resolution?.dpmm || (resolution?.dpi ? dpiToDpmm(resolution?.dpi) : 0) || DEFAULT_PRINT_RESOLUTION_DPMM;
  }

  getAllLayers() {
    // implementation only in inherited
  }

  componentDidMount() {
    // TODO: find a solution to wait until all is rendered (including all async action completed inside components like 'Symbol' -> onload)
    setTimeout(() => this.onRendered(), 1000);
  }

  // TODO: make sure that all is rendered. in some cases it can take several renderers
  componentDidUpdate() {
    this.onRendered();
  }

  onRendered() {
    const { page: currentPage } = this.getAllLayers();
    const firstPage = currentPage ? currentPage : this.props?.pages ? Object.values(this.props.pages)[0] : null;
    if (firstPage) {
      setTimeout(() => {
        this.props.onRendered(this.svgNodeRef.current.cloneNode(true));
      });
    }
  }

  // render shapes in cases
  // - for 'preview image' print - all shapes
  // - other - shields only
  mapShape(shapeObj, currentPage) {
    const { paperColor: originPaperColor, isContentPlacingDebugging } = this.props;
    const { primitiveType, primitiveProps, id, logicalType } = shapeObj;
    const clickable = false;
    const className = createClassName({ clickable });

    let shapeFill = null;
    if (this.isPreviewPrint) {
      switch (logicalType) {
        case 'shield':
        case 'solid':
          shapeFill = originPaperColor;
          break;
        case 'hole':
          shapeFill = '#ffffff';
          break;
      }
    } else {
      // content print
      const paperColor = 'transparent';

      switch (logicalType) {
        case 'shield':
        case 'solid':
          shapeFill = paperColor;
          break;
        case 'hole':
          shapeFill = '#ffffff';
          break;
      }
    }

    let hasShieldContent = false;
    if (logicalType === 'shield' && isContentPlacingDebugging) {
      hasShieldContent = hasShapeContent(shapeObj, currentPage);
    }

    return (
      <React.Fragment key={id}>
        <defs>
          <clipPath id={`${id}-clip-path`}>
            <rect x={primitiveProps.x} y={primitiveProps.y} width={primitiveProps.w} height={primitiveProps.h} />
          </clipPath>
        </defs>
        <PrimitiveRenderer
          style={{ fill: shapeFill, stroke: 'none', strokeWidth: 0 }}
          className={className}
          id={id}
          primitiveType={primitiveType}
          primitiveProps={primitiveProps}
          clickable={clickable}
          onFastClickTap={null}
          withMiddleLines={hasShieldContent}
          shapeObj={null}
        />
      </React.Fragment>
    );
  }

  mapSection(section, resizeType) {
    const { x, y, width, height, id, fillColor, ...formatProps } = section;
    const rectProps = { x, y, width, height };
    const { materialPrintParameters, paperColor } = this.props;
    let corrFormatProps = { ...formatProps };
    const isMultiColored = isMaterialMultiColored(materialPrintParameters);
    const isSectionRendererAvailable = corrFormatProps.hasBackground || corrFormatProps.text || corrFormatProps.code || corrFormatProps.glyph || corrFormatProps.image || corrFormatProps.shape;

    if (isSectionRendererAvailable) {
      const clickable = false;
      const draggable = false;
      const className = createClassName({ clickable, draggable });
      const { core: { isColored } } = store.getState();
      const corrFillColor = paperColor === fillColor ? null : (isMultiColored || isColored ? fillColor : colors.black);

      if (!isMultiColored && !isColored) {
        const corrPaperColor = corrFillColor ? corrFillColor : colors.white;
        const corrRibbonColor = corrPaperColor === colors.black ? colors.white : colors.black;

        corrFormatProps = { ...corrFormatProps, ...changeContentColor(corrRibbonColor) };
      } else {
        corrFormatProps = { ...corrFormatProps, ...changeContentColor(colors.black) };
      }

      return (
        <SectionRenderer
          key={`${id}-section-component`}
          id={id}
          className={className}
          svgStyleProps={{ stroke: 'none' }}
          fillColor={corrFillColor} // can be null for transparent
          resizeType={resizeType} // needed for recalculation of auto line break
          scaleCoef={1}
          rectProps={rectProps}
          formatProps={corrFormatProps}
          section={null}
          shield={null}
          isSectionSelected={false}
          hideSection={true}
          hideContent={false}
          clickable={clickable}
          skipFirstClickTapOnDbl={false}
          draggable={draggable}
          dragTransforms={null}
          resizable={false}
          onFastClickTap={null}
          onDblClickTap={null}
          onDragEnd={null}
          onResize={() => {}}
          groupIdPostfix="-print"
        />
      );
    } else {
      return null;
    }
  }

  renderSvg(rectProps, transforms, options, children) {
    // all calculations in MM units
    // transforms copying required to prevent data changes between printing of pages
    const _transforms = safeObjCopy(transforms || []);
    let sumRotation = _transforms.reduce((pv, cv) => pv + (cv.rotation || 0), 0);
    const isRotatedSize = (sumRotation / 90) % 2 === 1;
    const corrProps = isRotatedSize ? rotateRect90deg(rectProps.x, rectProps.y, rectProps.width, rectProps.height) : rectProps;

    const svgProps = {
      width: Math.floor(corrProps.width * this.printingQuality),
      height: Math.floor(corrProps.height * this.printingQuality),
      viewBox: `${corrProps.x} ${corrProps.y} ${corrProps.width} ${corrProps.height}`,
    };

    return (
      <svg
        ref={this.svgNodeRef}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        id="print-renderer-svg"
        x="0"
        y="0"
        preserveAspectRatio="none"
        {...svgProps}
      >
        <FontsLoaderEmbedded list={options.fonts} />
        <TransformsWrap rectProps={corrProps} transforms={_transforms} children={children} />
      </svg>
    );
  }

  buildRenderedShapes(page) {
    const { layouts } = page;
    const shieldsToRender = [];
  
    for (const layoutUid in layouts) {
      const { groups } = layouts[layoutUid];
      for (const groupUid in groups) {
        const { shields } = groups[groupUid];
        for (const shieldId in shields) {
          shieldsToRender.push(shields[shieldId]);
        }
      }
    }
  
    return shieldsToRender;
  }

  buildBorderLines({ currentPage, renderShapes }) {
    const { sections, layouts } = currentPage;
    const isMatrixShield = getMatrixShield(layouts);
    
    return Object.values(sections).reduce((acc1, section) => {
      if (isMatrixShield[section.shieldId]) {
        if (section.type === 'separator') {
          return acc1;
        }
  
        const currentShield = renderShapes.find(shield => shield.id === section.shieldId);
        const isMatrixBordersEnabled = currentShield.isMatrixBordersEnabled;
  
        if (!isMatrixBordersEnabled) {
          return acc1;
        }
  
        const safeStartX = getSafeValue(section.x);
        const safeStartY = getSafeValue(section.y);
        const safeEndX = getSafeValue(section.x + section.width);
        const safeEndY = getSafeValue(section.y + section.height);
  
        const square = {
          top: {
            start: { x: safeStartX, y: safeStartY },
            end: { x: safeEndX, y: safeStartY },
          },
          left: {
            start: { x: safeStartX, y: safeStartY },
            end: { x: safeStartX, y: safeEndY },
          },
          right: {
            start: { x: safeEndX, y: safeStartY },
            end: { x: safeEndX, y: safeEndY },
          },
          bottom: {
            start: { x: safeStartX, y: safeEndY },
            end: { x: safeEndX, y: safeEndY },
          },
        };         
  
        const bordersToRender = Object.entries(square).reduce((acc2, border) => {
          const [position, coords] = border;
  
          const primitiveProps = {
            start_point: coords.start,
            end_point: coords.end,
            outline_style: {},
          };
  
          if (isBorderIntersectToShield(currentShield, border) || isBorderIntersectToSibling(acc2, primitiveProps)) {
            return acc2;
          }
          return [
            ...acc2,
            {
              position,
              section,
              primitiveProps,
              style: { stroke: colors.black, strokeWidth: 0.2 },
            },
          ];
        }, []);
  
        return [...acc1, ...bordersToRender];
      }
      return acc1;
    }, []);
  }
}

const TransformsWrap = ({ transforms, rectProps, children }) => {
  const currTr = transforms.pop();
  return currTr ? (
    <g transform={buildTransformProp(currTr, rectProps)}>
      <TransformsWrap transforms={transforms} rectProps={rectProps} children={children} />
    </g>
  ) : (
    children
  );
};

const buildTransformProp = (transform, rectProps) => {
  let res = '';

  let corrProps = rectProps;
  let corrMirX = transform.mirrorX;
  let corrMirY = transform.mirrorY;
  if (transform.rotation === 90 || transform.rotation === 270) {
    corrProps = rotateRect90deg(rectProps.x, rectProps.y, rectProps.width, rectProps.height);
    const oldMirX = corrMirX;
    corrMirX = corrMirY;
    corrMirY = oldMirX;
  }

  if (corrMirX) {
    res = `${res} scale(-1, 1) translate(-${corrProps.x * 2 + corrProps.width}, 0)`;
  }
  if (corrMirY) {
    res = `${res} scale(1, -1) translate(0, -${corrProps.y * 2 + corrProps.height})`;
  }
  if (transform.rotation) {
    res = `${res} rotate(${transform.rotation} ${corrProps.x + corrProps.width / 2} ${corrProps.y + corrProps.height / 2})`;
  }
  if (transform.offsetCentralPoint) {
    res = `${res} translate(${transform.offsetCentralPoint.x || 0}, ${transform.offsetCentralPoint.y || 0})`;
  }

  return res;
};

const getSafeValue = value => Math.round(value * 100000) / 100000;

const isBorderIntersectToShield = (shield, border) => {
  const { w, h, y, x } = shield.primitiveProps;
  const [position, coords] = border;

  const safeShieldStartY = getSafeValue(y);
  const safeShieldEndY = getSafeValue(y + h);
  const safeShieldStartX = getSafeValue(x);
  const safeShieldEndX = getSafeValue(x + w);

  switch (position) {
    case 'top': {
      return coords.start.y === safeShieldStartY && coords.end.y === safeShieldStartY;
    }
    case 'bottom': {
      return coords.start.y === safeShieldEndY && coords.end.y === safeShieldEndY;
    }
    case 'left': {
      return coords.start.x === safeShieldStartX && coords.end.x === safeShieldStartX;
    }
    case 'right': {
      return coords.start.x === safeShieldEndX && coords.end.x === safeShieldEndX;
    }
    default: {
      return false;
    }
  }
};

const isBorderIntersectToSibling = (borders, primitiveProps) => {
  return borders.some(border => {
    return JSON.stringify(border.primitiveProps) === JSON.stringify(primitiveProps);
  });
};
